import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { appStorage } from '@sternum/web/services/appStorage';

export interface AlertsState {
  isPolling: boolean;
  isAlertsFiltersSidebarOpen: boolean;
  isDevicesAlertsFiltersSidebarOpen: boolean;
  isFleetsViewAlertsFiltersSidebarOpen: boolean;
  alertsSearchValue: string;

  areAlertNotesVisible: boolean;

  isAlertLogRowExpandedPerId: Record<string, boolean>;
  isAlertLogsViewInContext: boolean;
  isAlertLogsViewInContextTouched: boolean;
  areAlertLogsBeforeExpanded: boolean;
  areAlertLogsAfterExpanded: boolean;
  resetAlertLogsId: number;
}

const getInitialState: () => AlertsState = () => {
  return {
    isPolling: appStorage.getIsAlertsPolling() ?? false,
    isAlertsFiltersSidebarOpen: appStorage.getIsAlertsFiltersSidebarOpen() ?? true,
    isDevicesAlertsFiltersSidebarOpen: appStorage.getIsDevicesAlertsFiltersSidebarOpen() ?? true,
    isFleetsViewAlertsFiltersSidebarOpen: appStorage.getIsDevicesAlertsFiltersSidebarOpen() ?? false,
    alertsSearchValue: '',

    areAlertNotesVisible: appStorage.getAreAlertNotesVisible() ?? false,

    isAlertLogRowExpandedPerId: {},
    isAlertLogsViewInContext: true,
    isAlertLogsViewInContextTouched: false,
    areAlertLogsBeforeExpanded: true,
    areAlertLogsAfterExpanded: false,
    resetAlertLogsId: 1,
  };
};

export const alertsSlice = createSlice({
  name: 'alerts',
  initialState: getInitialState,
  reducers: {
    togglePollingAlertsAction(state) {
      state.isPolling = !state.isPolling;
      appStorage.setIsAlertsPolling(state.isPolling);
    },

    stopPollingAlertsAction(state) {
      state.isPolling = false;
      appStorage.setIsAlertsPolling(state.isPolling);
    },

    setIsAlertsFiltersSidebarOpenAction(state, action: PayloadAction<boolean>) {
      state.isAlertsFiltersSidebarOpen = action.payload;
    },

    toggleIsAlertsFiltersSidebarOpenAction(state) {
      state.isAlertsFiltersSidebarOpen = !state.isAlertsFiltersSidebarOpen;
    },

    setIsDevicesAlertsFiltersSidebarOpenAction(state, action: PayloadAction<boolean>) {
      state.isDevicesAlertsFiltersSidebarOpen = action.payload;
    },

    toggleIsDevicesAlertsFiltersSidebarOpenAction(state) {
      state.isDevicesAlertsFiltersSidebarOpen = !state.isDevicesAlertsFiltersSidebarOpen;
    },

    toggleIsFleetsViewAlertsFiltersSidebarOpenAction(state, action: PayloadAction<boolean | undefined>) {
      state.isFleetsViewAlertsFiltersSidebarOpen =
        action.payload === undefined ? !state.isFleetsViewAlertsFiltersSidebarOpen : action.payload;
    },

    setAlertsSearchValueAction(state, action: PayloadAction<string>) {
      state.alertsSearchValue = action.payload;
    },

    setAreAlertNotesVisible(state, action: PayloadAction<boolean>) {
      state.areAlertNotesVisible = action.payload;
    },

    toggleAreAlertNotesVisible(state) {
      state.areAlertNotesVisible = !state.areAlertNotesVisible;
    },

    toggleRowInAlertLogsTableAction: function (state, action: PayloadAction<string>) {
      const rowId = action.payload;
      const isRowExpanded = state.isAlertLogRowExpandedPerId[rowId];

      if (isRowExpanded) {
        delete state.isAlertLogRowExpandedPerId[rowId];
      } else {
        state.isAlertLogRowExpandedPerId[action.payload] = true;
      }
    },

    collapseAllInAlertLogsTableAction(state) {
      state.isAlertLogRowExpandedPerId = {};
    },

    toggleAlertLogsViewInContext(state, action: PayloadAction<boolean | undefined>) {
      state.isAlertLogsViewInContext =
        typeof action.payload === 'boolean' ? action.payload : !state.isAlertLogsViewInContext;
    },

    toggleIsAlertLogsViewInContextTouched(state, action: PayloadAction<boolean | undefined>) {
      state.isAlertLogsViewInContextTouched =
        action.payload === undefined ? !state.isAlertLogsViewInContextTouched : action.payload;
    },

    toggleAreAlertLogsBeforeExpanded(state, action: PayloadAction<boolean | undefined>) {
      state.areAlertLogsBeforeExpanded =
        action.payload === undefined ? !state.areAlertLogsBeforeExpanded : action.payload;
    },

    toggleAreAlertLogsAfterExpanded(state, action: PayloadAction<boolean | undefined>) {
      state.areAlertLogsAfterExpanded =
        action.payload === undefined ? !state.areAlertLogsAfterExpanded : action.payload;
    },

    resetAreAlertLogsBeforeAfterExpanded(state) {
      const initialState = getInitialState();

      state.areAlertLogsBeforeExpanded = initialState.areAlertLogsBeforeExpanded;
      state.areAlertLogsAfterExpanded = initialState.areAlertLogsAfterExpanded;
    },

    triggerResetAlertLogIdAction(state) {
      state.resetAlertLogsId = (state.resetAlertLogsId % 1000) + 1; // value from 1 to 1000
    },

    resetStateAction() {
      return getInitialState();
    },
  },
});

export const {
  togglePollingAlertsAction,
  stopPollingAlertsAction,
  setIsAlertsFiltersSidebarOpenAction,
  toggleIsAlertsFiltersSidebarOpenAction,
  setIsDevicesAlertsFiltersSidebarOpenAction,
  toggleIsDevicesAlertsFiltersSidebarOpenAction,
  toggleIsFleetsViewAlertsFiltersSidebarOpenAction,
  setAlertsSearchValueAction,
  setAreAlertNotesVisible,
  toggleAreAlertNotesVisible,
  toggleRowInAlertLogsTableAction,
  collapseAllInAlertLogsTableAction,
  toggleAlertLogsViewInContext,
  toggleIsAlertLogsViewInContextTouched,
  toggleAreAlertLogsBeforeExpanded,
  toggleAreAlertLogsAfterExpanded,
  resetAreAlertLogsBeforeAfterExpanded,
  triggerResetAlertLogIdAction,
  resetStateAction,
} = alertsSlice.actions;
